// slotKindToCpk — maps the EditorContext `slotKind` seam (GRA-8) to a
// CompletionPositionKind the resolver can dispatch on.
//
// G11: imports only the model-owned `EditorContextSlotKind` and the
// core/engines `CompletionPositionKind` — no web-tree-sitter, no tree types.
// This is the G11-legal consumption point for the projection's fine-grained
// slot classification. `null` means "the slot adds nothing"; the caller keeps
// whatever the clause/text machine produced.
//
// Window-frame intentionally has NO dedicated CompletionPositionKind: its win
// is a fixed keyword vocabulary emitted from a resolver-local
// `slotKind === 'window-frame'` check, so a new CPK would only add taxonomy
// surface. It maps to `null` here and is handled at the resolver, not via CPK.

import type { CompletionPositionKind } from '@/core/engines';
import type { EditorContextSlotKind } from '@/features/sql-grammar/model';

export function slotKindToCpk(slot: EditorContextSlotKind): CompletionPositionKind | null {
  switch (slot) {
    case 'data-type':
    // A row-source COLUMNS entry's type slot IS a data-type position — the
    // separate slot kind exists only so `deriveContext.ts` can grant it the
    // named-clause override its `from`-clause home requires (see
    // `editorContextSlot.rowSourceColumns.ts`). Resolvers dispatch on the
    // shared `'data-type'` CPK and need no new branch.
    case 'row-source-column-type':
      return 'data-type';
    // The DDL constraint/reference slots map 1:1 to the cross-engine
    // CompletionPositionKind members the other 4 resolvers already dispatch on
    // (GRA-8 S3 — MSSQL parity). The slot seam carries the precise kind so the
    // resolver picks the correct emission (referenced-table columns vs the
    // CREATE TABLE in-progress columns vs the ALTER target's live columns).
    // FULLTEXT/SPATIAL index column-list (GRA8-ROLL-3) — maps to the same
    // `ddl-index-column` CPK the resolvers already dispatch on for plain
    // CREATE INDEX, so the resolver emits the indexed table's columns.
    case 'ddl-index-column':
      return 'ddl-index-column';
    case 'ddl-references-column-list':
      return 'ddl-references-column-list';
    case 'ddl-create-table-expression':
      return 'ddl-create-table-expression';
    case 'ddl-alter-table-constraint-column-list':
      return 'ddl-alter-table-constraint-column-list';
    case 'ddl-fk-target':
      return 'ddl-fk-target';
    // The remaining DDL slot kinds (index-method / trigger-event / on-conflict
    // target / create-trigger timing) map 1:1 to the CompletionPositionKind the
    // four token-seam resolvers already dispatch on — they were previously
    // sourced from the string-walk `classifyDdlPositionKind` override and now
    // flow through the token slot seam (plan19 T7).
    case 'ddl-index-method':
      return 'ddl-index-method';
    case 'ddl-trigger-event':
      return 'ddl-trigger-event';
    // `CREATE TRIGGER … UPDATE OF ▮ … ON <tbl>` — the owning table sits AFTER
    // the caret, so the resolvers bind it with a forward text scan. PG /
    // Oracle / SQLite only (MySQL and T-SQL have no `UPDATE OF` column list).
    case 'ddl-target-table-column':
      return 'ddl-target-table-column';
    case 'ddl-on-conflict-target':
      return 'ddl-on-conflict-target';
    case 'ddl-create-trigger-timing':
      return 'ddl-create-trigger-timing';
    // The NEW object's name slot after a `CREATE … <object-type>` head. Maps
    // 1:1; every resolver dispatches it to a namespace-only emission (schemas /
    // databases, engine default first) — the position takes a name that does
    // not exist yet, so relation/column/type candidates are all wrong there.
    case 'ddl-create-object-name':
      return 'ddl-create-object-name';
    // SQL/PGQ GRAPH_TABLE slots (O2) — map 1:1 to the CompletionPositionKind
    // members the graph-aware resolvers dispatch on (Oracle: Task 20; PG:
    // Task 24). The `graph-element-property` slot carries the element variable
    // via the projection's existing `dottedBase`, so no extra payload is needed
    // through this adapter.
    case 'graph-name':
      return 'graph-name';
    case 'graph-label':
      return 'graph-label';
    case 'graph-element-property':
      return 'graph-element-property';
    case 'graph-columns':
      return 'graph-columns';
    // Value-slot plan T5b: maps 1:1 to the 'scalar-value' CPK. Unlike every
    // other kind above, `deriveContext.ts` routes this one EXCLUSIVELY through
    // the capability-gated third slot-seam gate (D4) — never the unknown/DDL
    // gates — so the mapping alone changes nothing while the production
    // capability table (`valueSlotCapabilities.ts`) is empty.
    case 'scalar-value':
      return 'scalar-value';
    // Predicate-head plan P3 (PH-D2): maps 1:1 to the 'predicate-head' CPK.
    // Like 'scalar-value' above, `deriveContext.ts` routes this kind
    // EXCLUSIVELY through its own capability-gated fourth slot-seam gate —
    // never the unknown/DDL gates — so the mapping alone changes nothing
    // while the production capability table
    // (`predicateHeadCapabilities.ts`) is empty. (P2 shipped a temporary
    // null bridge here; this is the P1-sol F1 flip.)
    case 'predicate-head':
      return 'predicate-head';
    case 'window-frame':
    case 'none':
      return null;
  }
}
